// เช็ครูปถ่ายติดบัตรด้วย MediaPipe FaceLandmarker (แทน face-api.js เดิมที่เช็คได้แค่ "มีหน้า 1 หน้า")
// เกณฑ์ตามที่ อ.ปาริยาสรุป: หน้าตรง ไม่กลับหัว ไม่ถ่ายระยะไกล รูปเดี่ยว
// ฝั่ง client เป็นแค่ UX ชั้นแรก — server (tcas65 helpers/picture-check) ตรวจซ้ำเป็น authority

// Landmark indices ของ MediaPipe Face Mesh (478 จุด)
const RIGHT_EYE_OUTER = 33; // ขวาของเจ้าของหน้า = ซ้ายของภาพ (ภาพไม่ mirror)
const LEFT_EYE_OUTER = 263;
const NOSE_TIP = 1;
const CHIN = 152;

// เกณฑ์ตัดสิน (องศา/สัดส่วน) — ตั้งหลวมกันปฏิเสธรูปดี ๆ; รูปก้ำกึ่งปล่อยให้ server/manual review จัดการ
const MAX_ROLL_DEGREES = 20; // เอียงศีรษะเกินนี้ = ไม่ใช่หน้าตรง
const MAX_YAW_RATIO = 0.3; // จมูกเบี่ยงจากกึ่งกลางตา (เทียบระยะห่างตา) เกินนี้ = หันข้าง
const MIN_FACE_HEIGHT_RATIO = 0.12; // กรอบหน้าเตี้ยกว่า 12% ของความสูงภาพ = ถ่ายระยะไกล
// หน้าที่สูงไม่ถึง 40% ของหน้าใหญ่สุดไม่นับเป็นคนที่สอง (ตราโรงเรียนบนเสื้อ — เคส 1909803346270) — เกณฑ์เดียวกับ server
const SECONDARY_FACE_MIN_RATIO = 0.4;

const faceHeight = (face) => {
  const ys = face.map((p) => p.y);
  return Math.max(...ys) - Math.min(...ys);
};

export const FACE_ERROR_MESSAGES = {
  no_face: 'ไม่พบใบหน้าในรูป กรุณาใช้รูปถ่ายหน้าตรงของตนเอง',
  many_faces: 'พบหลายใบหน้าในรูป กรุณาใช้รูปถ่ายเดี่ยวของตนเอง',
  upside_down: 'รูปกลับหัว กรุณาใช้รูปถ่ายที่วางถูกทิศทาง',
  not_frontal: 'กรุณาใช้รูปถ่ายหน้าตรง ไม่หันข้างหรือเอียงศีรษะ',
  too_far: 'ใบหน้าในรูปเล็กเกินไป กรุณาใช้รูปถ่ายครึ่งตัวที่เห็นใบหน้าชัดเจน',
};

// ประเมินจาก landmark ล้วน ๆ (ไม่พึ่ง transformation matrix) — คืน error code หรือ null ถ้าผ่าน
// แยกเป็น pure function ให้ unit test ยิง geometry สังเคราะห์ได้โดยไม่ต้องโหลดโมเดล
export const assessFaceLandmarks = (faceLandmarks, { width, height }) => {
  if (!faceLandmarks || faceLandmarks.length === 0) return 'no_face';
  const tallest = Math.max(...faceLandmarks.map(faceHeight));
  const faces = faceLandmarks.filter((face) => faceHeight(face) >= tallest * SECONDARY_FACE_MIN_RATIO);
  if (faces.length > 1) return 'many_faces';

  // landmark เป็นพิกัด normalized [0..1] — คูณขนาดภาพก่อนคิดมุม ไม่งั้นภาพผอม/แบนทำมุมเพี้ยน
  const lm = faces[0].map((p) => ({ x: p.x * width, y: p.y * height }));
  const eyeR = lm[RIGHT_EYE_OUTER];
  const eyeL = lm[LEFT_EYE_OUTER];
  const nose = lm[NOSE_TIP];
  const chin = lm[CHIN];
  const eyeMid = { x: (eyeR.x + eyeL.x) / 2, y: (eyeR.y + eyeL.y) / 2 };

  // กลับหัว: ตาต้องอยู่เหนือคาง (แกน y ของภาพชี้ลง)
  if (eyeMid.y > chin.y) return 'upside_down';

  // roll: มุมของเส้นตา (หน้าตรงปกติ ~0°) — เกิน 90° คือกลับหัว/ตะแคง
  const roll = (Math.atan2(eyeL.y - eyeR.y, eyeL.x - eyeR.x) * 180) / Math.PI;
  if (Math.abs(roll) > 90) return 'upside_down';
  if (Math.abs(roll) > MAX_ROLL_DEGREES) return 'not_frontal';

  // yaw: หน้าตรงจมูกต้องอยู่กึ่งกลางระหว่างตา — วัดระยะเบี่ยงตามแนวเส้นตา เทียบระยะห่างตา
  const eyeDx = eyeL.x - eyeR.x;
  const eyeDy = eyeL.y - eyeR.y;
  const interOcular = Math.hypot(eyeDx, eyeDy);
  const yawRatio = ((nose.x - eyeMid.x) * eyeDx + (nose.y - eyeMid.y) * eyeDy) / (interOcular * interOcular);
  if (Math.abs(yawRatio) > MAX_YAW_RATIO) return 'not_frontal';

  // ถ่ายระยะไกล: วัดจากกรอบ landmark ทั้งใบหน้าเทียบความสูงภาพ
  if (faceHeight(lm) / height < MIN_FACE_HEIGHT_RATIO) return 'too_far';

  return null;
};

let landmarkerPromise = null;
const getFaceLandmarker = () => {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      // lazy: wasm ~11MB + model ~3.6MB โหลดเฉพาะตอนผู้ใช้เลือกรูปจริง (แบบเดียวกับ face-api เดิม)
      const { FilesetResolver, FaceLandmarker } = await import('@mediapipe/tasks-vision');
      const fileset = await FilesetResolver.forVisionTasks('/mediapipe/wasm');
      return FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: '/mediapipe/face_landmarker.task' },
        runningMode: 'IMAGE',
        numFaces: 2, // ต้องการแค่รู้ว่า "เกิน 1 หน้า" — ขอ 2 พอ
      });
    })();
    landmarkerPromise.catch(() => {
      landmarkerPromise = null; // โหลดพลาด (เน็ตสะดุด) — ครั้งหน้าลองใหม่
    });
  }
  return landmarkerPromise;
};

// คืนข้อความ error ภาษาไทย หรือ null ถ้าผ่าน
// ถ้าตัว checker เองพัง (โหลดโมเดลไม่ได้ ฯลฯ) = ผ่าน (fail-open) เพราะ server ตรวจซ้ำอยู่แล้ว
const FACE_CHECK_TIMEOUT_MS = 15000;
export const checkPictureFace = async (file) => {
  const url = URL.createObjectURL(file);
  let active = true;
  let timer;
  try {
    const check = async () => {
      const img = document.createElement('img');
      img.src = url;
      await img.decode();
      if (!active) return null;
      const landmarker = await getFaceLandmarker();
      if (!active) return null;
      const result = landmarker.detect(img);
      const code = assessFaceLandmarks(result.faceLandmarks, {
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      return code ? FACE_ERROR_MESSAGES[code] : null;
    };
    // G49-2: ใช้เพดานเดียวกันทุกหน้า โหลดรูป/โมเดลค้างให้ตรวจต่อที่ server
    return await Promise.race([
      check(),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('face check timeout')), FACE_CHECK_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    console.warn('face check unavailable, deferring to server', error);
    return null;
  } finally {
    active = false;
    clearTimeout(timer);
    URL.revokeObjectURL(url);
  }
};
